import { useCallback, useEffect, useMemo, useState } from 'react';
import { Col, Form, Row, Tag } from 'antd';
import { Link, useLocation, useParams } from 'react-router-dom';
import { Button } from 'components';
import { EditIcon } from 'icons';
import { useTranslation } from 'react-i18next';
import { FileImageOutlined, LinkOutlined } from '@ant-design/icons';
import ErrorHelper from 'helpers/ErrorHelper';
import { useApi } from 'hooks/useApi';
import { useNavigate } from 'hooks/useNavigate';
import SsoUsersRoles from 'libs/api/ssoUsersRoles';
import styles from './main/style.module.scss';
import SsoUsers from 'libs/api/ssoUsers';
import { useRecoilValue } from 'recoil';
import { getFilters } from '../../../../../hooks/useShops';
import uuid from 'react-uuid';

const api = new SsoUsersRoles('notification_models');
const notificationsApi = new SsoUsersRoles('notifications');
const usersApi = new SsoUsers();

const aditionalPaginatonParams = {
  pageSize: 1000,
};

const usePresentNotifications = (isMainPage = true) => {
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { id } = useParams();
  const [premisionsGroups, setPremisionsGroups] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const filtersData = useRecoilValue(getFilters);
  const [getLinkLoading, setGetLinkLoading] = useState(false);

  const {
    data,
    singleItem: singleShop,
    loading,
    setLoading,
    onChangeTableFilter,
    onDelete,
    updateInProcess,
    setUpdateInProcess,
    fetchList,
    setSingleItem,
  } = useApi({
    resourceKey: 'notification_models',
    singleResourceKey: 'notification_model',
    api,
    isMainPage: !id,
    aditionalPaginatonParams,
  });
  const handleCreateRole = useCallback(
    async (values, idFromTable) => {
      setUpdateInProcess(true);
      try {
        const data = {
          ...values,
        };
        if (idFromTable) {
          await api.put(idFromTable, data);
        } else {
          if (!id || id === 'create') {
            data.notification_model_ids = [];
            await api.create({ data });
            navigate('/sso/notification/page/present-notifications');
          } else {
            await api.put(id, { data });
            navigate('/sso/notification/page/present-notifications');
          }
        }
        await fetchList();
      } catch (e) {
        form.setFields(ErrorHelper.getErrorsForForm(e?.response?.data || {}));
      } finally {
        setUpdateInProcess(false);
      }
    },
    [form, navigate, setUpdateInProcess, id],
  );
  const filters = useMemo(() => {
    return [
      {
        title: t('Titolo'),
        type: 'input',
        name: 'by_title',
      },
    ];
  }, [t]);

  const openLink = useCallback(async (id) => {
    setGetLinkLoading(true);
    try {
      const {
        data: { rimail_link },
      } = await api.get(id);
      console.log('data=', rimail_link);
      window.open(rimail_link, '_blank');
    } catch (e) {
      console.log(e);
    } finally {
      setGetLinkLoading(false);
    }
  }, []);

  const columns = useMemo(() => {
    return [
      {
        title: t('Nome'),
        dataIndex: 'title',
      },
      {
        title: t('Gruppi '),
        dataIndex: 'notifications',
        render: (data, row) => {
          if (data.length <= 3) {
            return (
              <Row gutter={[8, 8]}>
                {data.map((i) => {
                  const onClose = (id) => {
                    const notification_ids = data
                      .filter((j) => j.id !== id)
                      .map((j) => j.id);
                    handleCreateRole({ notification_ids }, row.id);
                  };
                  return (
                    <Col key={uuid()}>
                      <Tag
                        className={styles.tag}
                        closable
                        onClose={() => onClose(i.id)}
                      >
                        {i.title}
                      </Tag>
                    </Col>
                  );
                })}
              </Row>
            );
          } else {
            return (
              <Row gutter={[8, 8]}>
                {data.slice(0, 3).map((i) => {
                  const onClose = (id) => {
                    const notification_ids = data
                      .filter((j) => j.id !== id)
                      .map((j) => j.id);
                    handleCreateRole({ notification_ids }, row.id);
                  };
                  return (
                    <Col key={i.id}>
                      <Tag
                        className={styles.tag}
                        closable
                        onClose={() => onClose(i.id)}
                      >
                        {i.title}
                      </Tag>
                    </Col>
                  );
                })}
                <Tag className={styles.tag}>+ {data.slice(3).length}</Tag>
              </Row>
            );
          }
        },
      },
      {
        title: 'Link',
        dataIndex: 'id',
        render: (id) => {
          return (
            <Button
              loading={getLinkLoading}
              type='primary'
              icon={<LinkOutlined />}
              onClick={() => openLink(id)}
            >
              {t('Open')}
            </Button>
          );
        },
      },
      {
        title: 'Image',
        dataIndex: 'id',
        render: (id) => {
          return (
            <Link to={`${pathname}/${id}/image`}>
              <Button
                loading={getLinkLoading}
                type='primary'
                icon={<FileImageOutlined />}
              >
                {t('Flusso')}
              </Button>
            </Link>
          );
        },
      },
      {
        title: '',
        dataIndex: 'address',
        align: 'right',
        width: 120,
        render: (data, row) =>
          (row.can_update !== false || row.can_destroy !== false) && (
            <Row gutter={16} justify={'end'}>
              {row.can_update !== false && (
                <Col>
                  <Link to={`${pathname}/${row.id}/edit`}>
                    <Button type={'secondary'} icon={<EditIcon />} />
                  </Link>
                </Col>
              )}
            </Row>
          ),
      },
    ];
  }, [t, pathname, openLink, getLinkLoading]);

  useEffect(() => {
    if (id && singleShop) {
      try {
        let fieldsValues = [];
        let user_ids = [];
        for (let key in singleShop) {
          if (key === 'notifications') {
            user_ids = [
              ...user_ids,
              ...(singleShop[key]?.map((i) => i.id) || []),
            ];
          } else if (key !== 'id' && key !== 'extrastock_map') {
            fieldsValues.push({
              name: key,
              value: singleShop[key],
            });
          }
        }
        fieldsValues.push({
          name: 'notification_ids',
          value: user_ids,
        });
        form.setFields(fieldsValues);
      } catch (e) {
        console.log(e);
      }
    }
  }, [id, singleShop, form]);

  useEffect(() => {
    (async () => {
      try {
        const {
          data: { notifications = [] },
        } = await notificationsApi.list({
          params: {
            per: 1000,
            exclude_user_role: 4,
          },
        });
        setNotifications(notifications);
      } catch (e) {
        console.log(e);
      }
    })();
  }, []);

  const onUploadFile = async (id, file) => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('image', file);
      const { data } = await api.updateNotificationModelFile(id, formData);
      setSingleItem(data.notification_model);
    } catch (e) {
      console.log(e);
    } finally {
      setLoading(false);
    }
  };

  const onSaveImageNote = async (id, note) => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('image_note', note);
      const { data } = await api.updateNotificationModelFile(id, formData);
      setSingleItem(data.notification_model);
    } catch (e) {
      console.log(e);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteImage = async () => {
    setLoading(true);
    try {
      const response = await api.removeAttachmentsNotificationModel(
        singleShop.id,
        singleShop.image_id,
      );
      setSingleItem({
        ...singleShop,
        image_id: null,
        image: null,
      });
    } catch (e) {
      console.log(e);
    } finally {
      setLoading(false);
    }
  };

  return {
    columns,
    data,
    singleShop,
    loading,
    updateInProcess,
    onChangeTableFilter,
    form,
    filters,
    premisionsGroups,
    handleCreateRole,
    onDelete,
    notifications,
    shoppingCenters: filtersData?.shopping_centers || [],
    onUploadFile,
    handleDeleteImage,
    onSaveImageNote,
  };
};

export default usePresentNotifications;
